/**
 * Manage ignored auto-correct rules
 * Prevents system from suggesting corrections that have been marked as incorrect
 */
import logger from '../utils/logger';

import { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { X, AlertCircle } from 'lucide-react';

export default function IgnoredCorrectionsManager({ onClose, darkMode }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'ignored_correction_rules'));
      const rulesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRules(rulesData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (error) {
      logger.error('Error loading ignored rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'ignored_correction_rules', id));
      setRules(rules.filter(r => r.id !== id));
    } catch (error) {
      logger.error('Error deleting rule:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className={`rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col transition-colors duration-200 ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`p-6 border-b flex justify-between items-center transition-colors duration-200 ${
          darkMode
            ? 'bg-blue-900/30 border-blue-800'
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              Ignored Correction Rules
            </h2>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Corrections that were marked as incorrect and should not be suggested
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded transition-colors ${
              darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
            }`}
          >
            <X className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Loading rules...
            </div>
          ) : rules.length === 0 ? (
            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No ignored rules yet.</p>
              <p className="text-sm mt-1">
                When you decline a correction suggestion, it will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map(rule => (
                <div
                  key={rule.id}
                  className={`border rounded-lg p-4 transition-colors duration-200 ${
                    darkMode
                      ? 'bg-gray-700/50 border-gray-600'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Address
                      </div>
                      <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                        {rule.address}
                      </div>
                    </div>
                    <div>
                      <div className={`text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Unwanted Suggestion
                      </div>
                      <div className={`font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                        {rule.suggestedLocation} ❌
                      </div>
                    </div>
                  </div>
                  
                  <div className={`mt-3 pt-3 border-t text-xs ${
                    darkMode ? 'border-gray-600 text-gray-400' : 'border-gray-200 text-gray-600'
                  }`}>
                    <div>
                      <strong>Correct Location:</strong> {rule.correctLocation}
                    </div>
                    <div className="mt-1">
                      <strong>Reason:</strong> {rule.reason || 'User declined suggestion'}
                    </div>
                    <div className="mt-1">
                      <strong>Added:</strong> {new Date(rule.createdAt).toLocaleDateString()}
                    </div>
                  </div>

                  <button
                    onClick={() => handleDelete(rule.id)}
                    className={`mt-3 px-3 py-1 text-xs rounded border transition-colors ${
                      darkMode
                        ? 'border-red-800 text-red-400 hover:bg-red-900/20'
                        : 'border-red-300 text-red-600 hover:bg-red-50'
                    }`}
                  >
                    Remove Rule
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t flex justify-end transition-colors duration-200 ${
          darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
        }`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 border rounded transition-colors ${
              darkMode
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-100'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
